import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  notification,
  Spin,
  Upload,
  message,
  Button,
  Form,
  Alert,
  Image,
  Progress,
} from "antd";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { withTranslation } from "react-i18next";
import Apiauth from "../../../api/Apiauth";

const { Dragger } = Upload;

const Upslip = ({ handleChangeLoading, value,  t, fetchData }) => {
  const formRef = useRef(null);

  // States
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    ;
  }, []);

  const openNotification = (type, title, msg) => {
    notification[type]({
      message: title,
      description: msg,
    });
  };

  const handleFileChange = (info) => {
    let newFileList = [...info.fileList].slice(-1); // Only keep the last file

    const file = newFileList[0];
    if (file && file.originFileObj) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        openNotification(
          "error",
          "File Read Error",
          "ไม่สามารถอ่านไฟล์รูปภาพได้"
        );
        setPreviewImage(null);
        setFileList([]);
        setUploadProgress(0);
      };
      reader.readAsDataURL(file.originFileObj);
    } else {
      setPreviewImage(null);
    }

    setFileList(newFileList);

    // Auto-submit when a file is selected and read successfully
    if (newFileList.length > 0 && newFileList[0].status === "done") {
      onFinish();
    } else if (newFileList.length === 0) {
      resetState();
    }
  };

  const resetState = () => {
    setApiResponse(null);
    setApiError(null);
    setFileList([]);
    setPreviewImage(null);
    setUploadProgress(0);
    if (formRef.current) {
      formRef.current.resetFields();
    }
  };

  const customUploadRequest = ({ file, onSuccess, onError }) => {
    // We are handling the actual Axios POST in onFinish,
    // so this just tells Ant Design Upload component that the "upload" is done.
    onSuccess("ok");
  };

  const onFinish = async () => {
    handleChangeLoading(true);
    setLoading(true);
    setApiResponse(null);
    setApiError(null);
    setUploadProgress(0);

    if (fileList.length === 0 || !fileList[0].originFileObj) {
      message.error("กรุณาอัปโหลดรูปสลิป.");
      setLoading(false);
      handleChangeLoading(false);
      return;
    }

    if (!value?.referenceId) {
      openNotification("error", "เกิดข้อผิดพลาด", "ไม่พบข้อมูล Reference ID");
      setLoading(false);
      handleChangeLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("referenceId", value.referenceId);
    formData.append("uuid", value.uuid);
    formData.append("customerAccountNo", value.customerAccountNo);
    formData.append("amount", value.amount);
    formData.append("file", fileList[0].originFileObj);

    try {
      // Axios configuration with progress handling
      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      const res = await Apiauth.getUploadSlip(formData, config);

      if (res.data.success) {
        openNotification("success", "ตรวจสอบสำเร็จ", res.data.message);
        setApiResponse(res.data.message);
      } else {
        openNotification("error", "เกิดข้อผิดพลาด", res.data.message);
        setApiError(res.data.message);
      }
    } catch (err) {
      console.error("Error checking slip:", err);
      let errorMessage = "เกิดข้อผิดพลาดในการตรวจสอบรายการ";
      if (err.response) {
        errorMessage =
          err.response.data?.message || JSON.stringify(err.response.data);
      } else if (err.request) {
        errorMessage = "ไม่ได้รับการตอบกลับจากเซิร์ฟเวอร์ กรุณาลองอีกครั้ง.";
      } else {
        errorMessage = err.message;
      }
      openNotification("error", "ตรวจสอบไม่สำเร็จ", errorMessage);
      setApiError(errorMessage);
    } finally {
      setTimeout(() => {
        setLoading(false);
        handleChangeLoading(false);
        if (apiResponse) {
          resetState(); // Clear form only on success
          fetchData()
        }
      }, 1000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    openNotification("error", "ข้อมูลไม่ถูกต้อง", "กรุณาอัปโหลดรูปสลิป");
  };

  const draggerProps = {
    name: "file",
    listType: "picture-card",
    multiple: false,
    fileList: fileList,
    customRequest: customUploadRequest,
    onChange: handleFileChange,
    onRemove: resetState,
    beforeUpload: (file) => {
      const isJpgOrPng =
        file.type === "image/jpeg" || file.type === "image/png";
      if (!isJpgOrPng) {
        message.error("คุณสามารถอัปโหลดไฟล์ JPG/PNG เท่านั้น!");
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error("รูปภาพต้องมีขนาดน้อยกว่า 2MB!");
      }
      return isJpgOrPng && isLt2M;
    },
    showUploadList: false,
  };

  return (
    <>
      <div className="p-3">
        {apiResponse && (
          <Alert
            message={t("Verification Result")}
            description={apiResponse.message || JSON.stringify(apiResponse)}
            type="success"
            showIcon
            className="mt-3 mb-3"
          />
        )}

        {apiError && (
          <Alert
            message={t("Error Occurred")}
            description={apiError}
            type="error"
            showIcon
            className="mt-3 mb-3"
          />
        )}

        <Spin spinning={loading} tip={t("Verifying...")}>
          <Form
            layout="vertical"
            ref={formRef}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              name="file"
              rules={[
                {
                  required: true,
                  message: t("Please upload a slip image!"),
                },
              ]}
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            >
              {previewImage ? (
                <div
                  className="col-lg-12 col-md-12 col-sm-12"
                  style={{ textAlign: "center", marginBottom: "15px" }}
                >
                  <h5>{t("Preview")} :</h5>
                  <Image
                    src={previewImage}
                    alt="Preview"
                    style={{ maxHeight: "200px", border: "1px solid #ddd" }}
                    preview={false}
                  />
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div
                      style={{
                        marginTop: "10px",
                        width: "80%",
                        margin: "10px auto",
                      }}
                    >
                      <Progress percent={uploadProgress} />
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      justifyContent: "center",
                      gap: "15px"
                    }}
                  >
                    <Button shape="circle" onClick={onFinish}>
                      <ReloadOutlined />
                    </Button>
                    <Button danger shape="circle" onClick={resetState}>
                      <DeleteOutlined />
                    </Button>
                  </div>
                </div>
              ) : (
                <Dragger
                  maxCount={1}
                  {...draggerProps}
                  style={{
                    border: "none",
                    boxShadow:
                      "0 4px 10px 0 rgba(0,0,0,0.2),0 4px 20px 0 rgba(0,0,0,0.19)",
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: "14px" }}>
                    {t("Click here to upload your slip.")}
                  </p>
                  <p className="ant-upload-hint">
                    {t("Supports JPG/PNG up to 2MB.")}
                  </p>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div style={{ padding: "0 20px", marginTop: "10px" }}>
                      <Progress percent={uploadProgress} />
                    </div>
                  )}
                </Dragger>
              )}
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </>
  );
};

const mapStateToProps = ({ mailInboxReducer }) => ({
  isInbox: mailInboxReducer.isInbox,
});

export default connect(mapStateToProps, {})(withTranslation()(Upslip));
